/**
 * The message composer — one implementation, every role.
 *
 * Guarantees:
 *  • sticky, always visible, never covered by nav or the mobile keyboard
 *  • auto-expanding textarea (1 → 6 rows) with a hard max height
 *  • 16px font on mobile so iOS Safari never zooms the viewport on focus
 *  • Enter sends on pointer devices, newline on touch (enterKeyHint="send")
 *  • attachments with validation, thumbnails and removal
 *  • optional emoji picker, reply/edit context strips, upload progress
 *  • 44px targets, aria labels, disabled/locked states
 */

import React from 'react';
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  Edit3,
  Loader2,
  Lock,
  Paperclip,
  Reply,
  Send,
  Smile,
  X,
} from 'lucide-react';
import type { MessagingMessage, PendingAttachment } from './types';
import { IconButton, MessagingStrip } from './primitives';
import { PendingAttachments } from './Attachments';
import { useIsTouchDevice } from './hooks';
import {
  ALLOWED_ATTACHMENT_EXTENSIONS,
  MAX_ATTACHMENT_BYTES,
  formatFileSize,
  messageBodyText,
} from './utils';

const EMOJIS = [
  '👍', '🙏', '👏', '🎉', '✅', '❤️', '😊', '😀', '😅', '😂',
  '🤝', '💪', '🔥', '⭐', '📎', '📄', '📅', '⏰', '📌', '💡',
  '🤔', '😢', '😮', '🙌', '👀', '✍️', '📣', '🚀', '🏁', '❓',
];

export interface MessageComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  sending?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Renders a read-only "conversation closed" state with an optional action. */
  locked?: boolean;
  lockedTitle?: string;
  lockedDescription?: string;
  lockedAction?: React.ReactNode;

  attachments?: PendingAttachment[];
  onAttachmentsAdded?: (files: PendingAttachment[]) => void;
  onAttachmentRemoved?: (id: string) => void;
  allowAttachments?: boolean;
  /**
   * Attachment extensions the composer offers and accepts. Defaults to
   * the full list (support chat — uploads are brokered server-side).
   * 1:1 chat passes the subset Firebase Storage rules accept for direct
   * uploads, so users are never offered a type whose upload always fails.
   */
  allowedExtensions?: string[];
  allowCamera?: boolean;
  allowEmoji?: boolean;

  replyTo?: MessagingMessage | null;
  onCancelReply?: () => void;
  editing?: MessagingMessage | null;
  onCancelEdit?: () => void;

  uploadProgress?: number | null;
  error?: string | null;
  onDismissError?: () => void;
  /** Re-sends the failed message. Shown as "Try again" in the error strip. */
  onRetrySend?: () => void;
  notice?: string | null;
  onDismissNotice?: () => void;

  keyboardOpen?: boolean;
  /** Extra controls (canned replies, mentions, templates…) rendered inline. */
  extraActions?: React.ReactNode;
  autoFocus?: boolean;
  maxLength?: number;
}

async function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

export function MessageComposer({
  value,
  onChange,
  onSend,
  sending = false,
  disabled = false,
  placeholder = 'Type a message…',
  locked = false,
  lockedTitle = 'Conversation closed',
  lockedDescription = 'This conversation is archived.',
  lockedAction,
  attachments = [],
  onAttachmentsAdded,
  onAttachmentRemoved,
  allowAttachments = true,
  allowedExtensions = ALLOWED_ATTACHMENT_EXTENSIONS,
  allowCamera = false,
  allowEmoji = true,
  replyTo,
  onCancelReply,
  editing,
  onCancelEdit,
  uploadProgress = null,
  error,
  onDismissError,
  onRetrySend,
  notice,
  onDismissNotice,
  keyboardOpen = false,
  extraActions,
  autoFocus = false,
  maxLength = 5000,
}: MessageComposerProps) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const cameraInputRef = React.useRef<HTMLInputElement>(null);
  const [emojiOpen, setEmojiOpen] = React.useState(false);
  const [localError, setLocalError] = React.useState<string | null>(null);
  const [charCount, setCharCount] = React.useState(0);
  const isTouch = useIsTouchDevice();

  const canSend = (!!value.trim() || attachments.length > 0) && !sending && !disabled && !locked;

  /* Auto-size the textarea from its content. */
  const resize = React.useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, []);

  React.useEffect(() => {
    resize();
  }, [value, resize]);

  React.useEffect(() => {
    setCharCount(value.length);
  }, [value]);

  React.useEffect(() => {
    if (autoFocus && !isTouch) textareaRef.current?.focus();
  }, [autoFocus, isTouch]);

  React.useEffect(() => {
    if (editing) {
      textareaRef.current?.focus();
    }
  }, [editing]);

  /* Close the emoji panel on outside click / Escape. */
  React.useEffect(() => {
    if (!emojiOpen) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.msg-emoji-panel') && !target.closest('[data-emoji-trigger]')) {
        setEmojiOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setEmojiOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [emojiOpen]);

  const handleFiles = React.useCallback(
    async (fileList: FileList | File[] | null) => {
      if (!fileList || !onAttachmentsAdded) return;
      const accepted: PendingAttachment[] = [];
      const rejections: string[] = [];

      for (const file of Array.from(fileList)) {
        const ext = `.${(file.name.split('.').pop() || '').toLowerCase()}`;
        if (!allowedExtensions.includes(ext)) {
          rejections.push(`${file.name} — unsupported file type`);
          continue;
        }
        if (file.size > MAX_ATTACHMENT_BYTES) {
          rejections.push(`${file.name} — larger than ${formatFileSize(MAX_ATTACHMENT_BYTES)}`);
          continue;
        }
        try {
          const data = await readAsBase64(file);
          accepted.push({
            id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            name: file.name,
            size: file.size,
            mimeType: file.type,
            data,
            previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
            file,
          });
        } catch {
          rejections.push(`${file.name} — could not be read`);
        }
      }

      setLocalError(rejections.length ? rejections.join(' · ') : null);
      if (accepted.length) onAttachmentsAdded(accepted);
    },
    [onAttachmentsAdded, allowedExtensions]
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !isTouch) {
      event.preventDefault();
      if (canSend) onSend();
    }
    if (event.key === 'Escape') {
      if (editing) onCancelEdit?.();
      else if (replyTo) onCancelReply?.();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(event.clipboardData?.files || []);
    if (files.length && allowAttachments && onAttachmentsAdded) {
      event.preventDefault();
      void handleFiles(files);
    }
  };

  const insertEmoji = (emoji: string) => {
    const el = textareaRef.current;
    if (!el) {
      onChange(value + emoji);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + emoji + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + emoji.length;
      el.setSelectionRange(caret, caret);
      resize();
    });
  };

  if (locked) {
    return (
      <div className="msg-composer" data-locked="true">
        <div className="msg-composer-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span className="msg-avatar msg-avatar--sm msg-avatar--muted">
              <Lock size={14} />
            </span>
            <span style={{ minWidth: 0 }}>
              <span className="msg-strip-title" style={{ fontSize: 13 }}>
                {lockedTitle}
              </span>
              <span className="msg-strip-text">{lockedDescription}</span>
            </span>
          </span>
          {lockedAction}
        </div>
      </div>
    );
  }

  const activeError = error || localError;

  return (
    <div
      className={`msg-composer ${keyboardOpen ? 'msg-composer--keyboard' : ''}`.trim()}
      style={{ position: 'relative' }}
    >
      {replyTo && !editing && (
        <MessagingStrip
          tone="accent"
          icon={<Reply size={14} />}
          title={`Replying to ${replyTo.authorName || 'message'}`}
          text={messageBodyText(replyTo.body).slice(0, 120) || 'Attachment'}
          onDismiss={onCancelReply}
          dismissLabel="Cancel reply"
        />
      )}

      {editing && (
        <MessagingStrip
          tone="accent"
          icon={<Edit3 size={14} />}
          title="Editing message"
          text={messageBodyText(editing.body).slice(0, 120)}
          onDismiss={onCancelEdit}
          dismissLabel="Cancel edit"
        />
      )}

      {attachments.length > 0 && onAttachmentRemoved && (
        <PendingAttachments files={attachments} onRemove={onAttachmentRemoved} />
      )}

      {uploadProgress !== null && (
        <div className="msg-progress" role="progressbar" aria-valuenow={uploadProgress} aria-valuemin={0} aria-valuemax={100}>
          <div className="msg-progress-bar" style={{ width: `${uploadProgress}%` }} />
        </div>
      )}

      {activeError && (
        <MessagingStrip
          tone="danger"
          icon={<AlertTriangle size={14} />}
          text={activeError}
          onDismiss={() => {
            setLocalError(null);
            onDismissError?.();
          }}
        >
          {onRetrySend && (
            <button
              type="button"
              className="msg-strip-action"
              disabled={sending}
              onClick={() => {
                setLocalError(null);
                onDismissError?.();
                onRetrySend();
              }}
            >
              {sending ? 'Retrying…' : 'Try again'}
            </button>
          )}
        </MessagingStrip>
      )}

      {notice && !activeError && (
        <MessagingStrip
          tone="success"
          icon={<CheckCircle2 size={14} />}
          text={notice}
          onDismiss={onDismissNotice}
        />
      )}

      {emojiOpen && (
        <div className="msg-emoji-panel" role="listbox" aria-label="Insert emoji">
          {EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="msg-emoji-btn"
              onClick={() => insertEmoji(emoji)}
              aria-label={`Insert ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}

      <div className="msg-composer-row">
        {allowAttachments && onAttachmentsAdded && (
          <IconButton
            label="Attach file"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || sending}
          >
            <Paperclip size={18} />
          </IconButton>
        )}

        {allowCamera && onAttachmentsAdded && (
          <IconButton
            label="Take photo"
            className="msg-hide-desktop"
            onClick={() => cameraInputRef.current?.click()}
            disabled={disabled || sending}
          >
            <Camera size={18} />
          </IconButton>
        )}

        {extraActions}

        <div className="msg-composer-field">
          <textarea
            ref={textareaRef}
            className="msg-composer-input"
            value={value}
            rows={1}
            maxLength={maxLength}
            placeholder={placeholder}
            disabled={disabled}
            aria-label="Message"
            enterKeyHint={isTouch ? 'enter' : 'send'}
            inputMode="text"
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onInput={resize}
          />
          {allowEmoji && (
            <div className="msg-composer-footer flex items-center justify-between px-2 pt-1">
              <span className="msg-char-count text-xs text-muted">{charCount}/{maxLength}</span>
              <IconButton
                label={emojiOpen ? 'Close emoji picker' : 'Insert emoji'}
                small
                active={emojiOpen}
                data-emoji-trigger="true"
                onClick={() => setEmojiOpen((open) => !open)}
                disabled={disabled}
              >
                <Smile size={17} />
              </IconButton>
            </div>
          )}
        </div>

        <button
          type="button"
          className="msg-send"
          onClick={onSend}
          disabled={!canSend}
          aria-label={editing ? 'Save changes' : 'Send message'}
          title={editing ? 'Save changes' : 'Send message'}
        >
          {sending ? (
            <Loader2 size={18} className="msg-spin" />
          ) : editing ? (
            <CheckCircle2 size={18} />
          ) : (
            <Send size={17} style={{ transform: 'translateX(1px)' }} />
          )}
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={allowedExtensions.join(',')}
        style={{ display: 'none' }}
        onChange={(event) => {
          void handleFiles(event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(event) => {
          void handleFiles(event.target.files);
          event.target.value = '';
        }}
      />
    </div>
  );
}

export { X as ComposerCloseIcon };
